import { useEffect, useMemo, useRef, useState } from 'react';
import { CoachChatThread } from '../components/coach/CoachChatThread';
import { CoachErrorBoundary } from '../components/coach/CoachErrorBoundary';
import { CoachFormCheck } from '../components/coach/CoachFormCheck';
import { AffiliatePlacement } from '../components/AffiliatePlacement';
import { SaveCoachPlanCard } from '../components/coach/SaveCoachPlanCard';
import { PremiumPage } from '../components/premium';
import { StripeUnlockModal } from '../components/StripeUnlockModal';
import { useAuth } from '../auth/AuthContext';
import { api, ApiError } from '../lib/api';
import { formatPlanSummary, parseWorkoutPlanFromText } from '../lib/coachPlan';
import { loadCoachChatHistory, saveCoachChatHistory } from '../lib/coachChatHistory';
import { buildCoachContext, saveCoachMemory } from '../lib/coachInsights';
import { hasAiCoachingAccess } from '../lib/coachAccess';
import { PRO_TIER_NAME } from '../lib/productTier';
import { useCoachChatSession } from '../lib/coachChatSession';
import { useAiDailyQuota } from '../lib/aiDailyQuota';
import { localDateKey } from '../lib/dateKey';
import type { OptimizedSplitResult, WorkoutPlanResult } from '../coachTypes';
import type { Workout } from '../types';

type QuickAction = {
  label: string;
  kind: 'chat' | 'workout-plan' | 'scroll';
  prompt?: string;
  scrollTarget?: string;
};

const QUICK_ACTIONS: QuickAction[] = [
  {
    label: 'Review today',
    kind: 'chat',
    prompt:
      'Review my training and nutrition today. Use my actual logged data, be specific, and give 2–3 actionable suggestions.',
  },
  {
    label: 'Build program',
    kind: 'workout-plan',
    prompt: 'Build a complete multi-day training program tailored to my profile, goals, and recent training history.',
  },
  {
    label: 'Analyze nutrition',
    kind: 'chat',
    prompt: 'Analyze my nutrition today based on what I have logged. Tell me what to adjust for the rest of the day.',
  },
  {
    label: 'Recovery advice',
    kind: 'chat',
    prompt:
      'Give me recovery advice based on my recent training volume and schedule. Mention sleep, hydration, and whether I should train hard tomorrow.',
  },
  {
    label: 'Review progress',
    kind: 'chat',
    prompt:
      'Review my overall progress recently — training, nutrition, hydration, sleep, and goals. Use my logged data from the last two weeks and give honest trends plus 3–5 priorities.',
  },
];

function ToolRow({
  icon,
  title,
  subtitle,
  onClick,
}: {
  icon: string;
  title: string;
  subtitle: string;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="flex w-full items-center gap-3 border-b border-white/[0.06] py-3.5 text-left transition-colors last:border-0 hover:bg-white/[0.02]"
    >
      <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/[0.05] text-base">{icon}</span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-semibold text-[var(--text-primary)]">{title}</span>
        <span className="block truncate text-xs text-[var(--text-muted)]">{subtitle}</span>
      </span>
      <span className="shrink-0 text-sm text-[var(--text-muted)]">›</span>
    </button>
  );
}

export function CoachPage() {
  return (
    <CoachErrorBoundary title="Coach">
      <CoachPageInner />
    </CoachErrorBoundary>
  );
}

function CoachPageInner() {
  const { user } = useAuth();
  const [coachPaywallOpen, setCoachPaywallOpen] = useState(() => !hasAiCoachingAccess(user));
  const [err, setErr] = useState<string | null>(null);
  const [workouts, setWorkouts] = useState<Workout[]>([]);
  const [nutrition, setNutrition] = useState<{
    totals?: { proteinG?: number };
    targets?: { proteinG?: number };
  } | null>(null);
  const [inputFocused, setInputFocused] = useState(false);
  const [pendingPlan, setPendingPlan] = useState<{
    plan: WorkoutPlanResult;
    split: OptimizedSplitResult | null;
  } | null>(null);
  const [actionBusy, setActionBusy] = useState(false);
  const [toolsOpen, setToolsOpen] = useState(false);
  const chat = useCoachChatSession(user?.id);
  const { quota, refresh: refreshQuota } = useAiDailyQuota(hasAiCoachingAccess(user));
  const coachCap = !quota?.unlimited && (quota?.coachMessages.remaining ?? 1) <= 0;
  const composerRef = useRef<HTMLTextAreaElement | null>(null);

  useEffect(() => {
    if (hasAiCoachingAccess(user)) setCoachPaywallOpen(false);
  }, [user]);

  useEffect(() => {
    void api<Workout[]>('/api/workouts')
      .then((rows) =>
        setWorkouts(
          Array.isArray(rows)
            ? rows.map((w) => ({
                ...w,
                exercises: Array.isArray(w?.exercises) ? w.exercises : [],
              }))
            : [],
        ),
      )
      .catch(() => setWorkouts([]));
    void api<{ totals?: { proteinG?: number }; targets?: { proteinG?: number } }>(
      `/api/nutrition/day/${localDateKey()}`,
    )
      .then(setNutrition)
      .catch(() => setNutrition(null));
  }, []);

  const ctx = useMemo(() => {
    try {
      return buildCoachContext({
        displayName: user?.displayName ?? null,
        userId: user?.id,
        workouts,
        nutrition,
        sleepHours: null,
      });
    } catch (e) {
      console.error('[CoachPage] buildCoachContext failed', e);
      return {
        greeting: 'Hey there',
        headline: 'What do you want to improve today?',
        subline: 'Ask anything about training, nutrition, or recovery.',
        suggestions: ['Build my first workout', 'Analyze my nutrition today'],
        memory: null,
        emptyState: true,
      };
    }
  }, [user?.displayName, user?.id, workouts, nutrition]);

  const lastConvoSnippet = useMemo(() => {
    const prior = loadCoachChatHistory(user?.id);
    const lastUser = [...prior].reverse().find((t) => t.role === 'user');
    if (lastUser?.content.trim()) return lastUser.content.slice(0, 120).replace(/\s+/g, ' ').trim();
    if (ctx.memory?.summary) return ctx.memory.summary;
    const lastAssistant = [...prior].reverse().find((t) => t.role === 'assistant');
    if (lastAssistant?.content.trim()) return lastAssistant.content.slice(0, 120).replace(/\s+/g, ' ').trim();
    return 'Pick up where you left off';
  }, [user?.id, ctx.memory]);

  const hasPriorConvo = useMemo(
    () => loadCoachChatHistory(user?.id).length > 0 || Boolean(ctx.memory),
    [user?.id, ctx.memory],
  );

  const busy = chat.busy || actionBusy;
  const hasThread = chat.turns.length > 0 || busy;

  useEffect(() => {
    if (chat.turns.length) saveCoachChatHistory(user?.id, chat.turns);
    const last = chat.turns.filter((t) => t.role === 'assistant').at(-1);
    if (last && last.content.length > 20) {
      const snippet = last.content.slice(0, 120).replace(/\s+/g, ' ').trim();
      saveCoachMemory(user?.id, snippet);
    }
  }, [chat.turns, user?.id]);

  useEffect(() => {
    if (pendingPlan) return;
    const lastAssistant = chat.turns.filter((t) => t.role === 'assistant').at(-1);
    if (!lastAssistant) return;
    try {
      const parsed = parseWorkoutPlanFromText(lastAssistant.content);
      if (parsed) setPendingPlan({ plan: parsed, split: null });
    } catch (e) {
      console.warn('[CoachPage] plan parse failed', e);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- only when turns change
  }, [chat.turns]);

  function scrollToLatest() {
    requestAnimationFrame(() => chat.bottomRef.current?.scrollIntoView({ behavior: 'smooth', block: 'end' }));
  }

  async function sendPrompt(text: string) {
    if (coachCap) {
      setErr(`Daily limit reached (${quota?.coachMessages.limit ?? 5} coach messages). Resets tomorrow.`);
      return false;
    }
    const ok = await chat.send(text);
    void refreshQuota();
    if (ok) {
      scrollToLatest();
      composerRef.current?.focus();
    }
    return ok;
  }

  async function continueLastConvo() {
    const prior = loadCoachChatHistory(user?.id);
    const prompt =
      prior.length > 0
        ? 'Continue where we left off — reference what we discussed and give specific next steps.'
        : ctx.memory
          ? `We were discussing: "${ctx.memory.summary}". Please continue with specific follow-up advice using my recent training and nutrition data.`
          : 'Review my recent training and give me the top priorities for this week.';
    if (prior.length > 0) {
      const ok = await chat.send(prompt, prior);
      if (ok) scrollToLatest();
      return;
    }
    await sendPrompt(prompt);
  }

  async function runWorkoutPlanAction(prompt: string) {
    setActionBusy(true);
    setErr(null);
    chat.setTurns((prev) => [...prev, { role: 'user', content: prompt }]);
    try {
      const res = await api<{ plan: WorkoutPlanResult; split: OptimizedSplitResult | null }>('/api/ai/workout-plan', {
        method: 'POST',
        body: JSON.stringify({ additionalNotes: prompt }),
      });
      const summary = formatPlanSummary(res.plan);
      chat.setTurns((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: `Here's your program:\n\n${summary}\n\nTap **Save to My Programs** below to keep it.`,
        },
      ]);
      setPendingPlan({ plan: res.plan, split: res.split });
      scrollToLatest();
    } catch (e) {
      const message = e instanceof ApiError ? e.message : 'Could not build program.';
      setErr(message);
      chat.setTurns((prev) => [...prev, { role: 'assistant', content: message }]);
    } finally {
      setActionBusy(false);
    }
  }

  async function runQuickAction(action: QuickAction) {
    if (action.kind === 'scroll' && action.scrollTarget) {
      document.getElementById(action.scrollTarget)?.scrollIntoView({ behavior: 'smooth' });
      return;
    }
    if (action.kind === 'workout-plan' && action.prompt) {
      await runWorkoutPlanAction(action.prompt);
      return;
    }
    if (action.prompt) await sendPrompt(action.prompt);
  }

  if (!hasAiCoachingAccess(user)) {
    return (
      <PremiumPage title="Coach">
        {coachPaywallOpen ? (
          <StripeUnlockModal onClose={() => setCoachPaywallOpen(false)} />
        ) : (
          <div className="rounded-2xl border border-white/10 bg-[#121827] p-6 text-center">
            <p className="text-sm text-[var(--text-secondary)]">
              Coaching is locked. Upgrade to {PRO_TIER_NAME} to unlock.
            </p>
            <button
              type="button"
              onClick={() => setCoachPaywallOpen(true)}
              className="mt-4 rounded-2xl bg-[#4F8CFF] px-6 py-3 text-sm font-semibold text-white"
            >
              Unlock
            </button>
          </div>
        )}
      </PremiumPage>
    );
  }

  function runToolPrompt(prompt: string) {
    setToolsOpen(false);
    void sendPrompt(prompt);
  }

  return (
    <div className="mx-auto w-full max-w-2xl px-3 md:px-4">
      <div
        className="-mb-[var(--mobile-nav-clearance)] flex flex-col overflow-hidden pt-[max(calc(env(safe-area-inset-top,0px)+0.35rem),0.35rem)] md:mb-0"
        style={{ height: 'calc(100dvh - var(--mobile-nav-bar-height) - env(safe-area-inset-bottom, 0px))' }}
      >
        <header className="flex shrink-0 items-center gap-3 border-b border-white/[0.06] px-1 pb-3">
          <div className="flex h-10 w-10 items-center justify-center rounded-full bg-violet-500/20 text-sm font-bold text-violet-300">
            C
          </div>
          <div className="min-w-0 flex-1">
            <p className="text-sm font-semibold text-[var(--text-primary)]">Coach</p>
            <p className="truncate text-xs text-[var(--text-muted)]">
              {busy ? 'Typing…' : hasThread ? 'Online' : ctx.greeting}
            </p>
          </div>
          <button
            type="button"
            onClick={() => setToolsOpen(true)}
            className="shrink-0 rounded-full border border-white/12 bg-white/[0.05] px-3 py-1.5 text-[11px] font-semibold text-[var(--text-primary)]"
          >
            Tools
          </button>
        </header>

        <div className="nice-scroll min-h-0 flex-1 overflow-y-auto px-1 py-4">
          {!hasThread ? (
            <div className="flex min-h-full flex-col justify-end gap-4 pb-2">
              <div className="px-2">
                <p className="font-display text-xl font-bold text-[var(--text-primary)]">{ctx.headline}</p>
                <p className="mt-1 text-sm text-[var(--text-muted)]">{ctx.subline}</p>
              </div>
              {hasPriorConvo ? (
                <button
                  type="button"
                  onClick={() => void continueLastConvo()}
                  className="mx-1 rounded-2xl border border-white/10 bg-white/[0.04] px-4 py-3 text-left"
                >
                  <span className="block text-[10px] font-semibold uppercase tracking-wide text-[var(--text-muted)]">
                    Last conversation
                  </span>
                  <span className="mt-0.5 block truncate text-xs text-[var(--text-secondary)]">
                    &ldquo;{lastConvoSnippet}&rdquo;
                  </span>
                </button>
              ) : null}
              <div className="flex flex-wrap gap-2 px-1">
                {QUICK_ACTIONS.map((action) => (
                  <button
                    key={action.label}
                    type="button"
                    disabled={busy || (action.kind === 'chat' && coachCap)}
                    onClick={() => void runQuickAction(action)}
                    className="rounded-full border border-violet-400/25 bg-violet-500/10 px-3.5 py-2 text-xs font-medium text-violet-100 disabled:opacity-50"
                  >
                    {action.label}
                  </button>
                ))}
              </div>
              <div className="flex flex-wrap gap-2 px-1">
                {ctx.suggestions.map((s) => (
                  <button
                    key={s}
                    type="button"
                    disabled={busy || coachCap}
                    onClick={() => void sendPrompt(s)}
                    className="rounded-full border border-dashed border-white/15 px-3 py-1.5 text-[11px] text-[var(--text-muted)] disabled:opacity-50"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
          ) : (
            <CoachChatThread turns={chat.turns} busy={busy} bottomRef={chat.bottomRef} />
          )}

          {pendingPlan ? (
            <div className="mt-3 px-1">
              <SaveCoachPlanCard
                plan={pendingPlan.plan}
                split={pendingPlan.split}
                onSaved={() => setPendingPlan(null)}
                onDismiss={() => setPendingPlan(null)}
              />
            </div>
          ) : null}

          {err ? (
            <p className="mt-3 rounded-2xl border border-red-500/30 bg-red-500/10 px-4 py-3 text-sm text-red-200">{err}</p>
          ) : null}
        </div>

        <div className="shrink-0 border-t border-white/[0.06] bg-[var(--bg-base)] px-1 pb-2 pt-2">
          {quota ? (
            <p className="mb-2 px-3 text-[11px] text-[var(--text-muted)]">
              {quota.unlimited
                ? 'Unlimited coach messages on this account.'
                : `${quota.coachMessages.remaining} of ${quota.coachMessages.limit} coach messages left today.`}
            </p>
          ) : null}
          {hasThread ? (
            <div className="mb-2 flex gap-2 overflow-x-auto pb-0.5 [-ms-overflow-style:none] [scrollbar-width:none] [&::-webkit-scrollbar]:hidden">
              {QUICK_ACTIONS.slice(0, 4).map((action) => (
                <button
                  key={action.label}
                  type="button"
                  disabled={busy || (action.kind === 'chat' && coachCap)}
                  onClick={() => void runQuickAction(action)}
                  className="shrink-0 rounded-full border border-white/10 bg-white/[0.04] px-3 py-1.5 text-[11px] font-medium text-[var(--text-secondary)] disabled:opacity-50"
                >
                  {action.label}
                </button>
              ))}
            </div>
          ) : null}
          <form
            className={`flex items-end gap-2 rounded-[28px] border bg-white/[0.05] p-1.5 pl-4 transition-all ${
              inputFocused
                ? 'border-violet-400/50 shadow-[0_0_0_3px_rgba(139,92,246,0.15)]'
                : 'border-white/10'
            }`}
            onSubmit={(e) => {
              e.preventDefault();
              void sendPrompt(chat.input);
            }}
          >
            <textarea
              ref={composerRef}
              rows={1}
              value={chat.input}
              onChange={(e) => {
                chat.setInput(e.target.value);
                const el = e.target;
                el.style.height = 'auto';
                el.style.height = `${Math.min(el.scrollHeight, 120)}px`;
              }}
              onFocus={() => setInputFocused(true)}
              onBlur={() => setInputFocused(false)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !e.shiftKey) {
                  e.preventDefault();
                  void sendPrompt(chat.input);
                }
              }}
              placeholder={coachCap ? 'Daily message limit reached' : 'Message…'}
              disabled={busy || coachCap}
              className="max-h-[120px] min-h-[44px] min-w-0 flex-1 resize-none bg-transparent py-2.5 text-[16px] leading-snug text-white outline-none placeholder:text-white/35"
            />
            <button
              type="submit"
              disabled={busy || coachCap || !chat.input.trim()}
              aria-label="Send message"
              className="mb-0.5 flex h-10 w-10 shrink-0 items-center justify-center rounded-full bg-violet-500 text-lg font-bold text-white shadow-[0_4px_16px_rgba(139,92,246,0.4)] transition hover:brightness-110 disabled:opacity-35"
            >
              {busy ? '…' : '↑'}
            </button>
          </form>
        </div>
      </div>

      {toolsOpen ? (
        <div className="fixed inset-0 z-40 flex flex-col justify-end" role="dialog" aria-modal="true" aria-label="AI tools">
          <button
            type="button"
            className="absolute inset-0 bg-black/55"
            aria-label="Close tools"
            onClick={() => setToolsOpen(false)}
          />
          <div
            className="relative z-10 mx-auto max-h-[min(78dvh,640px)] w-full max-w-2xl overflow-y-auto rounded-t-3xl border border-white/10 bg-[#0B101A] px-4 pb-[calc(var(--mobile-nav-clearance)+0.75rem)] pt-3 shadow-[0_-12px_40px_rgba(0,0,0,0.45)] md:pb-6"
          >
            <div className="mx-auto mb-3 h-1 w-10 rounded-full bg-white/20" />
            <div className="mb-3 flex items-center justify-between gap-3">
              <p className="text-sm font-semibold text-[var(--text-primary)]">AI tools</p>
              <button
                type="button"
                onClick={() => setToolsOpen(false)}
                className="rounded-full border border-white/10 px-3 py-1 text-[11px] font-medium text-[var(--text-secondary)]"
              >
                Close
              </button>
            </div>
            <div className="space-y-4">
              <div className="rounded-2xl border border-white/[0.08] bg-[#121827] px-4">
                <ToolRow
                  icon="📈"
                  title="Progress review"
                  subtitle="Training, nutrition, sleep & goals"
                  onClick={() =>
                    runToolPrompt(
                      'Review my overall progress recently — training, nutrition, hydration, sleep, and goals. Use my logged data and give honest trends plus 3–5 priorities.',
                    )
                  }
                />
                <ToolRow
                  icon="🍽"
                  title="Nutrition analysis"
                  subtitle="Review today's meals"
                  onClick={() =>
                    runToolPrompt('Analyze my nutrition today based on what I logged. Be specific with macros and gaps.')
                  }
                />
                <ToolRow
                  icon="💤"
                  title="Recovery report"
                  subtitle="Sleep, readiness and fatigue"
                  onClick={() =>
                    runToolPrompt(
                      'Give me a recovery report based on my recent training volume, frequency, and nutrition. Tell me if I should push or deload.',
                    )
                  }
                />
              </div>
              <AffiliatePlacement slot="inline" className="mt-2" />
              <CoachErrorBoundary title="Form check">
                <div id="form-check">
                  <CoachFormCheck userId={user?.id} onError={setErr} />
                </div>
              </CoachErrorBoundary>
              <p className="pb-2 text-center text-[10px] text-[var(--text-muted)]">Not medical advice</p>
            </div>
          </div>
        </div>
      ) : null}
    </div>
  );
}
